import { useEffect, useState, useMemo } from "react";
import UpperLayerTabLayout from "../../../Components/DetailsPageUpperLayer/UpperLayerTabLayout";
import StockBar from "../../../Components/DetailsPageUpperLayer/StockBar";
//import NotesDrawer from "../../../Components/DetailsPageUpperLayer/NotesDrawer";
import MonetaryImpactModal from "../../../Components/DetailsPageUpperLayer/MonetaryImpactModel";
import { getCurrencySymbol, formatValue } from "../../../utils/currencyUtils";
import { getCurrencyFullName } from "../../../utils/currencyUtils";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const InventoryHeader = ({ hProps, onOnHandClick, onTotalStockClick }) => {
  const [itemInfo, setItemInfo] = useState({});
  const [stockDetails, setStockDetails] = useState({});
  const [siteInfo, setSiteInfo] = useState({});
  const [showMonetaryModal, setShowMonetaryModal] = useState(false);
  const [monetaryRows, setMonetaryRows] = useState([]);
  const [monetaryColumns, setMonetaryColumns] = useState([]);

  const totalStockFields = useMemo(() => ["unrestricted", "qualityInspection", "restrictedStock",
    "blockedStock", "returns", "stockInTransfer"], []);

  const fieldLabels = {
    item: "Material Number",
    itemName: "Material Description",
    siteCode: "Plant Number",
    siteName: "Plant Name",
    onHandValue: "= On Hand",
    totalStockValue: "= Total Stock",
    cokeEOL: "Coke EOL",
    eow: "EOW",
    eom: "EOM",
    eoq: "EOQ",
    eoy: "EOY",
    npiDate: "NPI Date",
    lmd: "LMD",
    lastPIComputeTime: "Last PI Compute Time",
    inventoryUOM: "Inventory UOM",
    returnablePackagingNumber: "Returnable Packaging",
    standardCostUsd: "Standard Cost per UOM [USD (M)]",
    standardLocalCost: "Standard Cost per UOM [Local Currency]",
    warehouseLocation: "Storage Location",
    mrpController: "MRP Controller",
    materialGroupDesc: "Material Group Description",
    stock: "EOD Projected Inventory"
  };

  const InfoIconAndTooltip = ({ value, tooltipText }) => {
    return (
      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        {value}
        <Tooltip arrow title={tooltipText}>
          <InfoOutlinedIcon
            fontSize="small"
            style={{ color: "#1976d2", cursor: "pointer", height: "15px", width: "15px" }}
          />
        </Tooltip>
      </span>
    );
  };

  useEffect(() => {
    if (!hProps) return;

    const stockKeys = [
      "stock",
      "monetaryImpact",
      "eow",
      "eom",
      "eoq",
      "eoy",
      "unrestricted",
      "qualityInspection",
      "restrictedStock",
      "blockedStock",
      "returns",
      "onHandValue",
      "stockInTransfer",
      "totalStockValue",
      "standardCostUsd",
      "standardLocalCost",
      "lastPIComputeTime",
      "inventoryFeedDate",
      "cokeEOL",
      "npiDate"
    ];

    const itemInfoKeys = [
      "item",
      "itemName",
      "materialGroup",
      "externalMaterialGroup",
      "materialGroupDesc",
      "inventoryUOM",
      "temperatureConditions",
      "returnablePackagingNumber",
      "procurementType",
      "mrpController"
    ];

    const siteKeys = [
      "siteCode",
      "siteName",
      "region",
      "country",
      "segment",
      "category",
      "warehouseLocation",
    ];

    const itemObj = {};
    const stockObj = {};
    const siteObj = {};
    let value;

    Object.keys(hProps).forEach((key) => {
      if (itemInfoKeys.includes(key)) {
        itemObj[key] = hProps[key] ?? null;
      } else if (stockKeys.includes(key)) {
        if (key === "onHandValue" && typeof onOnHandClick === "function") {
          stockObj[key] = (
            <span style={{ color: "#1976d2", cursor: "pointer" }} onClick={onOnHandClick}>
              {formatValue(hProps[key])}
            </span>
          );
        } else if (key === "totalStockValue" && typeof onTotalStockClick === "function") {
          stockObj[key] = (
            <span style={{ color: "#1976d2", cursor: "pointer" }} onClick={onTotalStockClick}>
              {formatValue(hProps[key])}
            </span>
          );
        } else if (key === "monetaryImpact") {
          const rawValue =
            typeof hProps[key] === "string"
              ? formatValue(hProps[key].replace(/[^0-9.-]+/g, ""))
              : formatValue(hProps[key]);
          stockObj[key] = (
            <span
              style={{ color: "#1976d2", cursor: "pointer" }}
              onClick={() => handleMonetaryImpactClick(rawValue)}
            >
              <InfoIconAndTooltip value={formatValue(rawValue)} tooltipText="Future Implementation" />
            </span>
          );
        } else if (key === "stock") {
          stockObj[key] = hProps.stock ? (
            <div
              style={{
                display: "flex",            // makes StockBar + icon align horizontally
                justifyContent: "center",   // aligns StockBar in the center
                alignItems: "center",       // vertical alignment
                gap: "6px",                 // spacing between bar and icon
                minWidth: "250px",
                flexShrink: 0,
                flexBasis: "auto",
                overflow: "visible",
                marginLeft: "auto"
              }}
            >
              <StockBar
                stock={Number(hProps.stock?.current) || 0} // Convert to number
                min_stock={Number(hProps.stock?.min) || 0} // Convert to number
                max_stock={Number(hProps.stock?.max) || 0} // Convert to number
                projected_stock={Number(hProps.stock?.projected) || 0} // Convert to number
                showLabels={true}
                lebelColor="#000000"
                source="details"
              />
              <InfoIconAndTooltip
                value=""
                tooltipText="End of Current Day Projected Inventory"
              />
            </div>
          ) : null;
        } else if (key.includes("eow")) {
          value = formatValue(hProps[key]);
          stockObj[key] = <InfoIconAndTooltip value={value} tooltipText="End of Fiscal Week" />;
        } else if (key.includes("eom")) {
          value = formatValue(hProps[key]);
          stockObj[key] = <InfoIconAndTooltip value={value} tooltipText="End of Fiscal Month" />;
        } else if (key.includes("eoq")) {
          value = formatValue(hProps[key]);
          stockObj[key] = <InfoIconAndTooltip value={value} tooltipText="End of Fiscal Quarter" />;
        } else if (key.includes("eoy")) {
          value = formatValue(hProps[key]);
          stockObj[key] = <InfoIconAndTooltip value={value} tooltipText="End of Fiscal Year" />;
        } else if (key.toLowerCase().includes("cost")) {
          if (key.toLowerCase().includes("usd")) {
            value = `${getCurrencySymbol("USD")}${formatValue(hProps[key])}`;
            stockObj[key] = <InfoIconAndTooltip value={value} tooltipText="Based on Monthly Currency Conversion" />;
          }
          else if (hProps[key].currency) {
            value = `${getCurrencySymbol(hProps[key].currency)}${formatValue(hProps[key].value)}`;
            const symbol = getCurrencySymbol(hProps[key].currency);
            const fullName = hProps[key].currency ? getCurrencyFullName(hProps[key].currency) : hProps[key].currency;
            stockObj[key] = <InfoIconAndTooltip value={value} tooltipText={`Based on ${symbol} (${fullName})`} />;
          } else {
            value = formatValue(hProps[key].value);
            stockObj[key] = <InfoIconAndTooltip value={value} tooltipText="Local Cost" />;
          }
        } else if (totalStockFields.some(field => key.toLowerCase().includes(field.toLowerCase()))) {
          stockObj[key] = formatValue(hProps[key]);
        }
        else {
          stockObj[key] = hProps[key];
        }
      } else if (siteKeys.includes(key)) {
        siteObj[key] = hProps[key] ?? null;
      }
    });

    // Utility to ensure all keys exist with null default
    const seedWithDefaults = (src, keys) =>
      keys.reduce((acc, k) => {
        acc[k] = src?.[k] ?? "";                                        // null -> UI will render as "TBD"
        return acc;
      }, {});

    // Build objects with fixed schemas so labels always render
    setItemInfo(seedWithDefaults(itemObj, itemInfoKeys));
    setStockDetails(seedWithDefaults(stockObj, stockKeys));
    setSiteInfo(seedWithDefaults(siteObj, siteKeys));
  }, [hProps, onOnHandClick, onTotalStockClick, totalStockFields]);

  const handleMonetaryImpactClick = (value) => {
    if (value <= 0 || isNaN(value)) {
      setMonetaryColumns([{ field: "message", headerName: "Info", flex: 1 }]);
      setMonetaryRows([{ id: 1, message: "No results found." }]);
    } else {
      setMonetaryColumns([
        { field: "impactType", headerName: "Impact Type", flex: 1 },
        { field: "amount", headerName: "Amount ($)", flex: 1 },
      ]);
      setMonetaryRows([
        { id: 1, impactType: "Write-Off", amount: value / 2 },
        { id: 2, impactType: "Shrinkage", amount: value / 2 },
      ]);
    }
    setShowMonetaryModal(true);
  };

  return (
    <>
      <UpperLayerTabLayout
        firstHeading="Stock"
        secondHeading="Item"
        thirdHeading="Plant"
        firstBoxData={stockDetails}
        secondBoxData={itemInfo}
        thirdBoxData={siteInfo}
        title="Inventory Detail"
        fieldLabels={fieldLabels}
      />
      <MonetaryImpactModal
        open={showMonetaryModal}
        handleClose={() => setShowMonetaryModal(false)}
        rows={monetaryRows}
        columns={monetaryColumns}
      />
    </>
  );
};

export default InventoryHeader;
